import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Monitor, 
  CheckCircle2, 
  Clock,
  Phone,
  ArrowLeft,
  Users,
  Award,
  Laptop,
  Play,
  BarChart3,
  Globe
} from "lucide-react";

const courseDetails = {
  format: "למידה מקוונת",
  availability: "24/7 זמין",
  tracking: "מעקב התקדמות",
  certificate: "תעודה דיגיטלית"
};

const platformFeatures = [
  {
    icon: Play,
    title: "תוכן וידאו מקצועי",
    description: "סרטוני הדרכה באיכות גבוהה עם הדמיות והנפשות"
  },
  {
    icon: BarChart3,
    title: "מעקב התקדמות",
    description: "לוח בקרה מלא למנהלים עם סטטוס כל העובדים"
  },
  {
    icon: Award,
    title: "מבחנים ותעודות",
    description: "בחינות אוטומטיות והנפקת תעודות דיגיטליות"
  },
  {
    icon: Globe,
    title: "זמינות גלובלית",
    description: "גישה מכל מקום ובכל זמן, מחשב או סלולרי"
  }
];

const availableCourses = [
  {
    title: "בטיחות בעבודה - כניסה",
    duration: "60 דקות",
    description: "הדרכת כניסה בסיסית לכל עובד חדש"
  },
  {
    title: "בטיחות אש",
    duration: "45 דקות",
    description: "מניעת שריפות, שימוש במטפים, פינוי"
  },
  {
    title: "ארגונומיה במשרד",
    duration: "30 דקות",
    description: "תנוחת ישיבה, סידור עמדת עבודה"
  },
  {
    title: "בטיחות בדרכים",
    duration: "45 דקות",
    description: "נהיגה בטוחה, עייפות, הסחות דעת"
  },
  {
    title: "עזרה ראשונה",
    duration: "90 דקות",
    description: "טיפול בסיסי, החייאה, חבישות"
  },
  {
    title: "חומרים מסוכנים",
    duration: "60 דקות",
    description: "זיהוי, אחסון, טיפול בחומרים"
  }
];

const benefits = [
  {
    title: "חיסכון בזמן",
    description: "עובדים לומדים בזמן שנוח להם, ללא צורך בתיאום קבוצות"
  },
  {
    title: "חיסכון בעלויות",
    description: "ללא עלויות נסיעה, אולמות או מדריכים לכל הדרכה"
  },
  {
    title: "עקביות התוכן",
    description: "כל עובד מקבל בדיוק אותו תוכן באותה איכות"
  },
  {
    title: "תיעוד אוטומטי",
    description: "המערכת מתעדת אוטומטית את כל ההדרכות והציונים"
  },
  {
    title: "גמישות",
    description: "העובד יכול להפסיק ולהמשיך מאותה נקודה"
  },
  {
    title: "התאמה אישית",
    description: "ניתן להתאים את התוכן לצרכי הארגון"
  }
];

const faqs = [
  {
    question: "האם הדרכות דיגיטליות מוכרות על ידי משרד העבודה?",
    answer: "כן, הדרכות דיגיטליות מוכרות לרוב הנושאים, בתנאי שכוללות בחינה ותיעוד מתאים. חלק מההדרכות דורשות גם רכיב מעשי פרונטלי."
  },
  {
    question: "איך מתבצע המעקב אחרי העובדים?",
    answer: "מנהלים מקבלים גישה ללוח בקרה המציג את סטטוס כל העובדים - מי סיים, מי בתהליך, ומי טרם התחיל. ניתן לקבל התראות אוטומטיות."
  },
  {
    question: "האם ניתן להוסיף תוכן מותאם לארגון?",
    answer: "כן, ניתן להוסיף מודולים מותאמים לארגון, לשלב נהלים פנימיים ותמונות מסביבת העבודה בפועל."
  },
  {
    question: "מה קורה אם עובד נכשל במבחן?",
    answer: "העובד יכול לחזור על החומר ולנסות שוב. אין הגבלה על מספר הניסיונות, והמערכת מציגה למנהל את כל הניסיונות."
  }
];

export default function DigitalSafety() {
  return (
    <Layout>
      <Helmet>
        <title>הדרכת בטיחות דיגיטלית | Adam Safety</title>
        <meta name="description" content="פלטפורמת הדרכות בטיחות דיגיטלית - למידה מקוונת 24/7, מעקב התקדמות, מבחנים ותעודות. פתרון חסכוני וגמיש להדרכות בטיחות." />
        <link rel="canonical" href="https://adam-safety.com/training/digital-safety" />
        <meta property="og:title" content="הדרכת בטיחות דיגיטלית | Adam Safety" />
        <meta property="og:description" content="פלטפורמת הדרכות בטיחות דיגיטלית - למידה מקוונת 24/7, מעקב התקדמות, מבחנים ותעודות. פתרון חסכוני וגמיש להדרכות בטיחות." />
        <meta property="og:url" content="https://adam-safety.com/training/digital-safety" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="הדרכת בטיחות דיגיטלית | Adam Safety" />
        <meta name="twitter:description" content="פלטפורמת הדרכות בטיחות דיגיטלית - למידה מקוונת 24/7, מעקב התקדמות, מבחנים ותעודות. פתרון חסכוני וגמיש להדרכות בטיחות." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <Monitor className="w-5 h-5" />
              <span className="text-sm font-medium">הדרכה דיגיטלית</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              הדרכת בטיחות דיגיטלית
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              פלטפורמת למידה מקוונת מתקדמת להדרכות בטיחות - 
              זמינה 24/7, עם מעקב התקדמות ותיעוד אוטומטי
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Laptop className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.format}</span>
              </div>
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Clock className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.availability}</span>
              </div>
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Award className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseDetails.certificate}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  לפרטים והדגמה
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              יכולות הפלטפורמה
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {platformFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-service text-center"
              >
                <div className="icon-container mx-auto mb-4">
                  <feature.icon className="w-7 h-7" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Available Courses */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              קורסים זמינים
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {availableCourses.map((course, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-2xl p-6 border border-border"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-foreground">{course.title}</h3>
                  <span className="text-xs px-2 py-1 rounded-full bg-accent/20 text-accent">
                    {course.duration}
                  </span>
                </div>
                <p className="text-muted-foreground text-sm">{course.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              יתרונות הלמידה הדיגיטלית
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4"
              >
                <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <CheckCircle2 className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground mb-1">{benefit.title}</h3>
                  <p className="text-muted-foreground text-sm">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              שאלות נפוצות
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-2">{faq.question}</h3>
                <p className="text-muted-foreground">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              רוצים לראות הדגמה?
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              נשמח להציג לכם את הפלטפורמה ולהתאים פתרון לצרכים שלכם
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  קביעת הדגמה
                </Button>
              </Link>
              <Link to="/trainings">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  הדרכות נוספות
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}